import { Card, Skeleton, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import FilterDonut from "./FilterDonut";
import ReloadButton from "../../../common/reload-button/ReloadButton";
import DonutChart from "../../../common/PieChart";
import { useNotificationContext } from "../../../../context/notificationContext";
import { useProtectedAxiosInstance } from "../../../../hooks/axiosInstance";
import { useNavigate } from "react-router-dom";
import errorResolver from "../../../../utils/error-resolver";
import { format } from "date-fns";
import { filterOptions } from "./filterOptions";
import {
  BroadcastingDetailsRequest,
  BroadcastingReportListColumnFilterable,
  VoiceBroadCastReportSchema,
} from "../../../../services/Endpoints/broadcast";
import { FilterOperatorEnum } from "../../../../services/Schemas/ListQueryString";
import MuiLinearProgress from "../../common/MuiLinearProgress";

interface IBroadCastingMedSecProps {
  type?: string;
  id?: number | string;
  loading?: boolean;
  initialReports?: VoiceBroadCastReportSchema[] | null;
}

export default function BroadcastReactionReport({
  id,
  type,
  loading,
  initialReports = null,
}: Readonly<IBroadCastingMedSecProps>) {
  const [rawFilter, setRawFilter] = useState(filterOptions[0].value.start);

  const [reports, setReports] = useState<VoiceBroadCastReportSchema[]>([]);
  const [timeFilter, setTimeFilter] = useState<string[] | null>(null);
  const [waiting, setWaiting] = useState(false);
  const { setNotification } = useNotificationContext();
  const protectedInstance = useProtectedAxiosInstance();
  const navigate = useNavigate();
  const [reload, setReload] = useState(false);

  const handleReload = () => {
    setReload(!reload);
  };

  const getData = async () => {
    if (id) {
      try {
        setWaiting(true);
        const broadcastingDetailsRequest = new BroadcastingDetailsRequest(
          protectedInstance
        );
        const { data: _data } = await broadcastingDetailsRequest.report(
          {
            broadcastingNumber: id.toString(),
          },
          {
            filters: timeFilter
              ? [
                  {
                    column: BroadcastingReportListColumnFilterable.UPDATED_AT,
                    operator: FilterOperatorEnum.BETWEEN,
                    value: timeFilter,
                  },
                ]
              : [],
          }
        );
        setReports(_data.number_reports);
        setWaiting(false);
      } catch (error: any) {
        errorResolver({ error, setNotification });
        setWaiting(false);
        navigate("/404");
        console.error(error.message);
      }
    }
  };

  useEffect(() => {
    initialReports && getData();
  }, [timeFilter, reload]);

  useEffect(() => {
    initialReports && setReports(initialReports);
  }, [initialReports]);

  const handleTimeFilter = (rawFilter: { start: string; end: string }) => {
    if (!waiting) {
      setRawFilter(rawFilter.start);
      setTimeFilter([
        format(rawFilter.start, "yyyy-MM-dd HH:mm:ss"),
        format(rawFilter.end, "yyyy-MM-dd HH:mm:ss"),
      ]);
    }
  };

  const answered =
    reports &&
    reports.filter((itm) => itm.status == "answered" || itm.call_input);
  const answeredTotal =
    reports &&
    reports
      .filter((itm) => itm.status == "answered" || itm.call_input)
      .reduce((pre, cur) => pre + Number(cur.total), 0);

  const responseCount = reports
    .filter((itm) => itm.call_input)
    .reduce((prev, cur) => prev + Number(cur.total), 0);

  const countPercentage = isNaN(Math.ceil((responseCount * 100) / answeredTotal))?0:Math.ceil((responseCount * 100) / answeredTotal);

  return (
    <Stack
      sx={{
        mx: "0 !important",
      }}
      height="inherit"
      position="relative"
    >
      {waiting && <MuiLinearProgress />}
      {type != "simple" && (
        <Stack
          width="100%"
          sx={{
            mx: "0 !important",
          }}
          height="100%"
        >
          <Card
            component={Stack}
            bgcolor="#fff"
            width="100%"
            gap="10px"
            height="100%"
            sx={{
              mx: "0 !important",
              margin: "0 !important",
              borderRadius: "8px",
              py: "14px",
              px: "16px",
              borderColor: (theme) => theme.palette.others.borderColor,
              borderWidth: "1px",
              borderStyle: "solid",
              boxShadow: "none",
              flex: "1",
              // height: "inherit",
            }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" gap="10px" justifyContent="start">
                {!loading ? (
                  <FilterDonut
                    setOption={handleTimeFilter}
                    optionsList={filterOptions}
                    active={
                      filterOptions.filter(
                        (itm) => itm.value.start == rawFilter
                      )[0].key
                    }
                  />
                ) : (
                  Array(5)
                    .fill("")
                    .map((_, i) => (
                      <Skeleton
                        variant="rectangular"
                        width="80px"
                        height="20px"
                      />
                    ))
                )}
              </Stack>

              {!loading ? (
                <ReloadButton reloadAction={handleReload} />
              ) : (
                <Skeleton variant="rectangular" width="40px" height="40px" />
              )}
            </Stack>
            {!loading ? (
              <>
                <Typography
                  variant="h6MediumBold"
                  color={(thm) => thm.palette.accent.main}
                >
                  گزارش مشارکت مخاطبین
                </Typography>
                <Typography
                  variant="captionMediumBold"
                  whiteSpace="wrap"
                  color={(thm) => thm.palette.dominant.main}
                >
                  این نمودار نشان می‌دهد که {countPercentage}
                  {"% "} ({responseCount}) کاربران هنگام تماس عددی وارد کرده
                  اند. در حالی که {100 - countPercentage}
                  {"% "} ({answeredTotal - responseCount}) پاسخی نداده اند.
                </Typography>
              </>
            ) : (
              <>
                <Skeleton variant="rectangular" width="200px" height="20px" />
                <Skeleton variant="rectangular" width="500px" height="20px" />
              </>
            )}
            <Stack justifyContent="center" alignItems="center">
              {reports ? (
                <DonutChart
                  data={
                    answered.length > 0
                      ? [
                          {
                            name: `شرکت کنندگان در نظرسنجی`,
                            valueDisplay: ` مخاطب`,
                            color: "#3C9A4F",
                            value: responseCount,
                          },
                          {
                            name: ` عدم شرکت در نظرسنجی`,
                            valueDisplay: `مخاطب`,
                            color: "#ed0202",
                            value: answeredTotal - responseCount,
                          },
                        ]
                      : []
                  }
                  pieWrapSx={{
                    py: "10px",
                    flexDirection: "row-reverse",
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "end",
                    "& > :first-child": {
                      mr: "auto",
                    },
                  }}
                  legendSx={{
                    flexDirection: "column",
                  }}
                  loading={(waiting && reports.length == 0) || loading}
                />
              ) : (
                <Typography
                  sx={{
                    textAlign: "center",
                    width: "100%",
                    py: "30px",
                  }}
                >
                  {!waiting && <>دیتایی جهت نمایش وجود ندارد.</>}
                </Typography>
              )}
            </Stack>
          </Card>
        </Stack>
      )}
    </Stack>
  );
}
